import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Button, Dropdown, TextInput } from 'components/common'
import { MASTER_DATA_FORM_FIELDS } from 'utils/constants'
import AggregatorsDropdown from './aggregatorsDropdown'
import { updateMasterData } from 'utils/api/masterDataApi'
import toast from 'react-hot-toast'
import { getDepartment, getFamily, getSection } from 'utils/helpers'

const {
  ITEM_NUMBER,
  ACTIVE_ONLINE,
  BARCODE,
  DEPARTMENT,
  ERP_BRAND,
  ERP_ITEM_DESC,
  ERP_MARKETING_CODE,
  ERP_PACK_INFO,
  ERP_UOM,
  FAMILY,
  HIDE_AGGREGATORS,
  ONLINE_DESC,
  ONLINE_PACK_INFO,
  QTY_PER_UOM,
  SECTION,
  SUB_FAMILY,
  VAT_CLASS,
  VAT_PERCENTAGE,
} = MASTER_DATA_FORM_FIELDS

type Props = {
  catalogue: any
}

const MasterDataUpdateForm = ({ catalogue }: Props) => {
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...catalogue,
      barcode: catalogue._id,
      dept: getDepartment(catalogue?.dept),
      family: getFamily(catalogue?.family),
      section: getSection(catalogue?.section),
    },
  })

  const backToMasterData = () => {
    navigate(-1)
  }

  const handleUpdateMasterData = async (data: any) => {
    const updateToast = toast.loading('Updating Master Data...')
    try {
      await updateMasterData(catalogue._id, {
        activeOnline: data.activeOnline,
        hideAggregators: data.hideAggregators,
        onlineDesc: data.onlineDesc,
        onlinePackInfo: data.onlinePackInfo,
      })
      toast.success('Master Data Updated Successfully!', {
        id: updateToast,
      })
      reset(data)
      backToMasterData()
    } catch (error) {
      toast.error('Failed to update Master Data.', {
        id: updateToast,
      })
    }
  }

  return (
    <div className="rounded-lg border bg-white px-5 py-3 ">
      <div className="mx-auto max-w-5xl">
        <form onSubmit={handleSubmit(handleUpdateMasterData)}>
          <div className="grid grid-cols-2 gap-x-14 gap-y-3 md:grid-cols-3">
            {/* Item Number */}
            <TextInput
              register={register}
              name={ITEM_NUMBER.key}
              label={ITEM_NUMBER.label}
              disabled
            />

            {/* Barcode */}
            <TextInput
              register={register}
              name={BARCODE.key}
              label={BARCODE.label}
              disabled
            />

            {/* Department */}
            <TextInput
              name={DEPARTMENT.key}
              label={DEPARTMENT.label}
              register={register}
              disabled
            />

            {/* Section */}
            <TextInput
              name={SECTION.key}
              label={SECTION.label}
              register={register}
              disabled
            />

            {/* Family */}
            <TextInput
              name={FAMILY.key}
              label={FAMILY.label}
              register={register}
              disabled
            />

            {/* Sub Family */}
            <TextInput
              name={SUB_FAMILY.key}
              label={SUB_FAMILY.label}
              register={register}
              disabled
            />

            {/* ERP Item Description */}
            <TextInput
              name={ERP_ITEM_DESC.key}
              label={ERP_ITEM_DESC.label}
              register={register}
              disabled
            />

            {/* ERP UOM */}
            <TextInput
              name={ERP_UOM.key}
              label={ERP_UOM.label}
              register={register}
              disabled
            />

            {/* QTY ERP UOM */}
            <TextInput
              register={register}
              name={QTY_PER_UOM.key}
              label={QTY_PER_UOM.label}
              disabled
            />

            {/* ERP Pack Info */}
            <TextInput
              register={register}
              name={ERP_PACK_INFO.key}
              label={ERP_PACK_INFO.label}
              disabled
            />

            {/* ERP Brand */}
            <TextInput
              register={register}
              name={ERP_BRAND.key}
              label={ERP_BRAND.label}
              disabled
            />

            {/* ERP Marketing Code */}
            <TextInput
              register={register}
              name={ERP_MARKETING_CODE.key}
              label={ERP_MARKETING_CODE.label}
              disabled
            />

            {/* Vat Class */}
            <TextInput
              register={register}
              name={VAT_CLASS.key}
              label={VAT_CLASS.label}
              disabled
            />

            {/* Vat Percentage */}
            <TextInput
              register={register}
              name={VAT_PERCENTAGE.key}
              label={VAT_PERCENTAGE.label}
              disabled
            />

            {/* Hide Aggregators */}
            <AggregatorsDropdown
              defaultSelected={
                catalogue?.hideAggregators ??
                catalogue?.[HIDE_AGGREGATORS.key] ??
                []
              }
              resetForm={false}
              error={errors?.[HIDE_AGGREGATORS.key]?.message as string}
              setValue={setValue}
              name={HIDE_AGGREGATORS.key}
              label={HIDE_AGGREGATORS.label}
              isActive
            />

            {/* Online Description */}
            <TextInput
              register={register}
              name={ONLINE_DESC.key}
              label={ONLINE_DESC.label}
            />

            {/* Online Pack Info */}
            <TextInput
              register={register}
              name={ONLINE_PACK_INFO.key}
              label={ONLINE_PACK_INFO.label}
            />

            {/* Active Online */}
            <Dropdown
              name={ACTIVE_ONLINE.key}
              label={ACTIVE_ONLINE.label}
              setValue={setValue}
              defaultValue={catalogue?.[ACTIVE_ONLINE.key]}
              error={errors?.[ACTIVE_ONLINE.key]?.message as string}
              options={[
                { label: 'Yes', value: true, disabled: false },
                {
                  label: 'No',
                  value: false,
                  disabled: false,
                },
              ]}
            />
          </div>
          <div className="my-4 flex gap-5">
            <Button buttonText="Save" className="w-32" />
            <Button
              buttonText="cancel"
              variant="outline"
              type="button"
              onClick={backToMasterData}
              className="w-32"
            />
          </div>
        </form>
      </div>
    </div>
  )
}

export default MasterDataUpdateForm
